import * as React from 'react';
import { Body } from 'vet-bones/design-system/components';
import { formatDate } from 'vet-bones/utils';

import { ResourcePreviewHeader } from 'src/components/resourceDialog/ResourcePreviewHeader';
import {
  PreviewEmptyState,
  PreviewErrorState,
  PreviewLoadingState,
  PreviewNoSelectionState,
} from 'src/components/resourceDialog/ResourcePreviewStates';
import { ResourcePreviewTable } from 'src/components/resourceDialog/ResourcePreviewTable';
import { TEST_IDS } from 'src/constants/testIds';
import { ProjectVirtualGraph } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';

type VirtualGraphPreviewProps = {
  virtualGraph: ProjectVirtualGraph;
  resourceName: string;
  onResourceNameChange: (name: string) => void;
  onFieldLabelChange?: (value: string, label: string) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
  error?: string;
  /** Number of rows in the preview (for row count display) */
  rowCount?: number | null;
  /**
   * When true, the data source has resource previews disabled: show editable
   * column headers (from the catalog) with no sample rows, and hide the row
   * count. The refresh button is still shown (when `onRefresh` is provided) so
   * catalog columns can be re-fetched.
   */
  previewsDisabled?: boolean;
};

export const VirtualGraphPreview: React.VFC<VirtualGraphPreviewProps> = ({
  virtualGraph,
  resourceName,
  onResourceNameChange,
  onFieldLabelChange,
  onRefresh,
  isLoading,
  error,
  rowCount,
  previewsDisabled,
}) => {
  const lastModified = virtualGraph.dateLastUpdated
    ? formatDate(new Date(virtualGraph.dateLastUpdated))
    : null;

  const hasPartialData = !virtualGraph.allResultsIncluded;

  if (isLoading) {
    return <PreviewLoadingState />;
  }

  if (error) {
    return (
      <PreviewErrorState
        message={error}
        title={copy.components.virtualGraphPreviewDialog.errorTitle}
      />
    );
  }

  const hasNoTableSelected =
    typeof virtualGraph.from === 'object' && !virtualGraph.from.name;

  if (hasNoTableSelected) {
    return (
      <PreviewNoSelectionState
        subtitle={
          copy.components.virtualGraphPreviewDialog.noTableSelected.subtitle
        }
        title={copy.components.virtualGraphPreviewDialog.noTableSelected.title}
      />
    );
  }

  if (previewsDisabled) {
    const previewsDisabledCopy =
      copy.components.virtualGraphPreviewDialog.previewsDisabled;

    if (!virtualGraph.fields.length) {
      return (
        <PreviewNoSelectionState
          subtitle={previewsDisabledCopy.subtitle}
          title={previewsDisabledCopy.title}
        />
      );
    }

    return (
      <>
        <ResourcePreviewHeader
          nameInputId="virtual-graph-name-input"
          nameInputTestId={TEST_IDS.projectVirtualGraphPanel.resourceName}
          namePlaceholder={
            copy.components.virtualGraphPreviewDialog.resourceNamePlaceholder
          }
          onRefresh={onRefresh}
          onResourceNameChange={onResourceNameChange}
          refreshButtonTestId={
            TEST_IDS.projectVirtualGraphPanel.preview.refreshButton
          }
          resourceName={resourceName}
        />
        <ResourcePreviewTable
          emptyBodyContent={
            <div
              data-testid={
                TEST_IDS.projectVirtualGraphPanel.preview
                  .previewsDisabledMessage
              }
            >
              <Body size="md">{previewsDisabledCopy.title}</Body>
              <Body size="md" weight="light">
                {previewsDisabledCopy.subtitle}
              </Body>
            </div>
          }
          fields={virtualGraph.fields}
          onFieldLabelChange={onFieldLabelChange}
          rows={[]}
          testId={TEST_IDS.projectVirtualGraphPanel.preview.table}
        />
      </>
    );
  }

  if (!virtualGraph.fields.length) {
    return (
      <PreviewEmptyState
        message={
          copy.components.projectResourceDialog.projectVirtualGraphPanel.preview
            .errors.previewEmpty
        }
      />
    );
  }

  const rowCountText =
    rowCount !== null && rowCount !== undefined
      ? copy.components.projectResourceDialog.projectVirtualGraphPanel.form.previewRowCount(
          rowCount,
          Boolean(virtualGraph.allResultsIncluded)
        )
      : null;

  return (
    <>
      <ResourcePreviewHeader
        lastModified={lastModified}
        nameInputId="virtual-graph-name-input"
        nameInputTestId={TEST_IDS.projectVirtualGraphPanel.resourceName}
        namePlaceholder={
          copy.components.virtualGraphPreviewDialog.resourceNamePlaceholder
        }
        onRefresh={onRefresh}
        onResourceNameChange={onResourceNameChange}
        refreshButtonTestId={
          TEST_IDS.projectVirtualGraphPanel.preview.refreshButton
        }
        resourceName={resourceName}
        rowCountTestId={TEST_IDS.projectVirtualGraphPanel.preview.rowCount}
        rowCountText={rowCountText}
        showPartialDataWarning={hasPartialData}
      />
      <ResourcePreviewTable
        fields={virtualGraph.fields}
        onFieldLabelChange={onFieldLabelChange}
        rows={virtualGraph.previewRows}
        testId={TEST_IDS.projectVirtualGraphPanel.preview.table}
      />
    </>
  );
};
